<template>
  <div>
    <b-table
      v-if="!loading && !failed && items.length > 0"
      :items="items"
      :fields="tableFields"
      hover
      responsive
      thead-class="thead-dark"
      class="mb-0 ticket-list-table"
      tbody-tr-class="cursor-pointer"
      @row-clicked="onRowClicked"
    >
      <!-- Ticket Number + Subject combined -->
      <template v-slot:cell(ticketNumber)="data">
        <div>
          <div>
            <div class="d-flex align-items-center mb-1">
              <span><i class="fa fa-ticket mr-1"></i> <CopyText :text="data.item.ticketNumber" /></span>
              <b-badge
                v-if="data.item.unreadMessages"
                variant="danger"
                pill
                class="ml-2"
                v-b-tooltip.hover
                title="Unread Messages"
              >{{ data.item.unreadMessages }}</b-badge>
                
              <button
                class="badge badge-secondary p-1 ml-2"
                type="button"
                @click.stop="navigateToDetails(data.item.id)"
                v-b-tooltip.hover
                title="Open Details"
              >
                <i class="fa small fa-external-link"></i>
              </button>
            </div>
            <div class="font-weight-medium">{{ data.item.name }}</div>
          </div>
        </div>
      </template>

      <!-- Type -->
      <template v-slot:cell(type)="data">
        <b-badge variant="light" class="text-dark border">{{ data.item.type }}</b-badge>
      </template>

      <!-- Priority -->
      <template v-slot:cell(priority)="data">
        <div v-if="is('superadmin')" @click.stop>
          <b-form-select
            v-model="data.item.priority"
            :options="priorityList"
            size="sm"
            class="priority-select"
            @change="onPriorityChange(data.item.id, data.item.priority)"
          ></b-form-select>
        </div>
        <div v-else>
          <b-badge :variant="getPriorityVariant(data.item.priority)">
            {{ getPriorityLabel(data.item.priority) }}
          </b-badge>
        </div>
      </template>

      <!-- Status -->
      <template v-slot:cell(status)="data">
        <b-badge :variant="getStatusVariant(data.item.status, data.item.subStatus)">
          {{ getStatusLabel(data.item.status, data.item.subStatus) }}
        </b-badge>
      </template>

      <!-- Last Activity -->
      <template v-slot:cell(lastActivity)="data">
        <div class="d-flex">
          <div v-if="data.item.lastMessageDateTimeUtc">
            <small class="border-bottom pb-1 mb-1">{{ formatDate(data.item.lastMessageDateTimeUtc) }}</small>
            <div class="pt-2">
              <small class="text-muted" v-if="data.item.lastMessagePreview">
                <b class="text-dark">{{ data.item.lastMessageAuthorName }}:</b>
                {{ data.item.lastMessagePreview }}
              </small>
            </div>
          </div>
          <div v-else>
            <span class="text-muted">No replies yet</span>
          </div>
        </div>
      </template>

      <!-- Closed Date (only for completed tab) -->
      <template v-slot:cell(completedDateTimeUtc)="data">
        <div v-if="data.item.completedDateTimeUtc">
          <span>{{ formatDate(data.item.completedDateTimeUtc) }}</span>
        </div>
        <div v-else>
          <span class="text-muted">-</span>
        </div>
      </template>

      <!-- Created By -->
      <template v-slot:cell(userFullName)="data">
        <span>{{ data.item.userFullName }}</span>
      </template>
    </b-table>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2 text-muted">Loading tickets...</p>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !failed && items.length === 0" class="text-center py-5">
      <i class="fa fa-ticket-alt fa-3x text-muted mb-3"></i>
      <p class="text-muted">No tickets found</p>
    </div>

    <!-- Error State -->
    <div v-if="failed" class="text-center py-5">
      <b-alert show variant="danger">
        Failed to load tickets.
        <b-button variant="link" @click="$emit('pagination-changed')">Try again</b-button>
      </b-alert>
    </div>

    <!-- Pagination -->
    <div v-if="items.length > 0" class="d-flex justify-content-between align-items-center p-3 border-top">
      <small class="text-muted">
        Showing {{ (pagination.currentPage - 1) * pagination.perPage + 1 }}
        - {{ Math.min(pagination.currentPage * pagination.perPage, pagination.totalItems) }}
        of {{ pagination.totalItems }} tickets
      </small>
      <b-pagination
        v-model="localCurrentPage"
        :total-rows="pagination.totalItems"
        :per-page="pagination.perPage"
        size="sm"
        @change="onPageChange"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'TicketListTable',
  mixins: [myCommonDataMixin],
  props: {
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    failed: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 15
      })
    },
    showClosedDate: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      localCurrentPage: this.pagination.currentPage,
      priorityList: [
        { value: 1, text: 'Low' },
        { value: 2, text: 'Medium' },
        { value: 3, text: 'High' },
        { value: 4, text: 'Urgent' }
      ]
    };
  },

  computed: {
    tableFields() {
      var fields = [
        { key: 'ticketNumber', label: 'Ticket / Subject', sortable: false },
        { key: 'type', label: 'Type', sortable: false, thStyle: { width: '140px' } },
        { key: 'priority', label: 'Priority', sortable: false, thStyle: { width: '120px' } },
        { key: 'userFullName', label: 'Created By', sortable: false, thStyle: { width: '150px' } },
        { key: 'status', label: 'Status', sortable: false, thStyle: { width: '100px' } },
        { key: 'lastActivity', label: 'Last Activity', sortable: false, thStyle: { width: '440px' } }
      ];

      if (this.showClosedDate) {
        fields.push({
          key: 'completedDateTimeUtc',
          label: 'Closed On',
          sortable: false,
          thStyle: { width: '140px' }
        });
      }

      return fields;
    }
  },

  watch: {
    'pagination.currentPage': function(newVal) {
      this.localCurrentPage = newVal;
    }
  },

  methods: {
    navigateToDetails(ticketId) {
      this.$router.push({
        name: 'Support.NewTickets.View',
        params: { id: ticketId }
      });
    },
    onRowClicked(item) {
      this.$emit('row-clicked', item);
    },

    onPageChange(page) {
      this.pagination.currentPage = page;
      this.$emit('pagination-changed');
    },

    onPriorityChange(ticketId, priority) {
      this.$emit('priority-changed', ticketId, priority);
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      var date = dateStr;
      if (!dateStr.endsWith('Z')) {
        date = dateStr + 'Z';
      }
      return this.$moment(date).format('MMM Do YY, h:mm a');
    },

    getPriorityLabel(priority) {
      var labels = {
        0: 'Not Set',
        1: 'Low',
        2: 'Medium',
        3: 'High',
        4: 'Urgent'
      };
      return labels[priority] || 'Not Set';
    },

    getPriorityVariant(priority) {
      var variants = {
        0: 'secondary',
        1: 'secondary',
        2: 'info',
        3: 'warning',
        4: 'danger'
      };
      return variants[priority] || 'secondary';
    },

    getStatusLabel(status, subStatus) {
      if (subStatus && subStatus !== '') {
        return subStatus;
      }
      var labels = {
        'triage': 'Triage',
        'backlog': 'Backlog',
        'unstarted': 'Open',
        'started': 'In Progress',
        'canceled': 'Cancelled',
        'completed': 'Completed'
      };
      return labels[status] || status;
    },

    getStatusVariant(status, subStatus) {
      if (subStatus && subStatus !== '') {
        return 'info';
      }
      var variants = {
        'triage': 'warning',
        'backlog': 'secondary',
        'unstarted': 'primary',
        'started': 'info',
        'canceled': 'danger',
        'completed': 'success'
      };
      return variants[status] || 'secondary';
    }
  }
};
</script>

<style scoped>
.ticket-list-table tbody tr {
  cursor: pointer;
}

.ticket-list-table tbody tr:hover {
  background-color: #f8f9fa;
}

.priority-select {
  min-width: 100px;
}

.font-weight-medium {
  font-weight: 500;
}
</style>
